// src/pages/AboutUs.js

import React, { useEffect } from 'react';
import { motion } from 'framer-motion';  // Import Framer Motion
import { Link } from 'react-router-dom'; // Import Link
import HeroSectionAbout from '../components/HeroSectionAbout';
import AboutImageSlideshow from '../components/AboutImageSlideshow';

function AboutUs({ isAdmin, onLogout }) {
  // Check if we're on mobile
  const isMobile = typeof window !== 'undefined' ? window.innerWidth <= 768 : false;
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    // Fade in the entire page container
    <motion.div 
      style={styles.container}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      
      {/* Hero Section */}
      <HeroSectionAbout isAdmin={isAdmin} onLogout={onLogout} />

      {/* INTRO SECTION: Two-Column Layout with a slide-up effect */}
      {/* Removed as per request */}

      {/* BOTTOM CTA SECTION - 100% WIDTH (Fade & slide-up on scroll) */}
      <motion.section
        style={styles.bottomSection}
        initial={{ y: 40, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
      >
        {/* Background Image Slideshow */}
        <div style={styles.backgroundImageContainer}>
          <AboutImageSlideshow
            section="background"
            isAdmin={isAdmin}
            customImageStyle={styles.backgroundImage}
            customContainerStyle={styles.backgroundSlideshow}
          />
        </div>
        
        <div style={styles.bottomOverlay}>
          <h2 style={isMobile ? styles.bottomHeadingMobile : styles.bottomHeading}>
            HL'ADÁTE PRENÁJOM BYTU ALEBO AUTOMOBILU ?
          </h2>
          <p style={isMobile ? styles.bottomTextMobile : styles.bottomText}>
            Hľadáte nový nájom? Chcete si požičať auto? Vyberte si z našej ponuky a staňte sa naším spokojným klientom.<br />
            Ak hľadáte bývanie, tak ste na správnom mieste. Obráťte sa na nás.
          </p>
          <Link to="/nehnutelnosti" style={styles.buttonLink}>
            <button style={isMobile ? styles.bottomButtonMobile : styles.bottomButton}>
              Ponuka nehnuteľností
            </button>
          </Link>
        </div>
      </motion.section>
    </motion.div>
  );
}

export default AboutUs;

const styles = {
  // Main container
  container: {
    fontFamily: "'Inter', Helvetica, Arial, sans-serif",
    width: '100%',
    margin: 0,
    padding: 0,
    overflowX: 'hidden',
    position: 'relative',
    height: 'auto', // Remove any fixed height constraint
    minHeight: 'auto', // Remove any fixed min-height
  },

  // Intro Section Wrapper
  introSection: {
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '3rem',
    marginBottom: '2rem',
    marginTop: '2rem',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    backgroundColor: '#fff',
    boxSizing: 'border-box',
    '@media (max-width: 768px)': {
      padding: '1.5rem',
    },
  },
  heading: {
    fontSize: '2.5rem',
    color: '#003366',
    marginBottom: '1.5rem',
    textAlign: 'left',
    '@media (max-width: 768px)': {
      fontSize: '2rem',
    },
  },
  introRow: {
    display: 'flex',
    gap: '2rem',
    alignItems: 'stretch', // Changed from flex-start to stretch for equal height
    minHeight: '300px', // Set a minimum height for desktop view
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      gap: '1rem',
      minHeight: 'auto', // Remove min-height on mobile
    },
  },
  introLeft: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center', // Center the text vertically
    '@media (max-width: 768px)': {
      width: '100%',
    },
  },
  introRight: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '@media (max-width: 768px)': {
      width: '100%',
      marginTop: '1rem',
    },
  },
  introImage: {
    width: '100%', // Changed from maxWidth to width for better control
    height: '100%', // Make the image take the full height
    objectFit: 'cover', // Ensure the image covers the area nicely
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    maxHeight: '300px', // Set max height to match the content area
  },
  introImageContainer: {
    width: '100%',
    height: '100%',
    maxHeight: '300px',
  },
  paragraph: {
    fontSize: '1.1rem',
    lineHeight: 1.6,
    color: '#555',
    textAlign: 'left',
    margin: 0,
    '@media (max-width: 768px)': {
      fontSize: '1rem',
    },
  },

  // BOTTOM CTA SECTION - 100% WIDTH
  bottomSection: {
    position: 'relative',
    width: '100%',
    minHeight: '60vh',
    marginBottom: '2rem',
    overflow: 'hidden',
    '@media (max-width: 768px)': {
      minHeight: 'auto',
    },
  },
  backgroundImageContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
  },
  backgroundSlideshow: {
    width: '100%',
    height: '100%',
  },
  backgroundImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: 0,
  },
  bottomOverlay: {
    position: 'relative',
    zIndex: 2,
    width: '100%',
    minHeight: '60vh',
    background: `
      linear-gradient(
        45deg,
        rgba(44, 105, 160, 0.9) 0%,
        rgba(28, 63, 115, 0.9) 100%
      )
    `,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    textAlign: 'center',
    padding: '3rem 2rem',
    boxSizing: 'border-box',
    '@media (max-width: 768px)': {
      minHeight: 'auto',
      padding: '2rem 1rem',
    },
  },
  bottomHeading: {
    fontSize: '3rem',
    fontWeight: '700',
    marginBottom: '1rem',
  },
  bottomHeadingMobile: {
    fontSize: '1.5rem', // Smaller size for mobile
    fontWeight: '700',
    marginBottom: '0.8rem',
  },
  bottomText: {
    fontSize: '1.2rem',
    lineHeight: 1.6,
    marginBottom: '2rem',
    maxWidth: '700px',
  },
  bottomTextMobile: {
    fontSize: '0.9rem', // Smaller size for mobile
    lineHeight: 1.4,
    marginBottom: '1.5rem',
    maxWidth: '100%',
    padding: '0 10px',
  },
  buttonLink: {
    textDecoration: 'none',
  },
  bottomButton: {
    fontSize: '1.2rem',
    padding: '1rem 2rem',
    backgroundColor: 'white',
    color: 'black',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'opacity 0.3s ease',
  },
  bottomButtonMobile: {
    fontSize: '0.9rem',
    padding: '0.7rem 1.2rem',
    backgroundColor: 'white',
    color: 'black',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'opacity 0.3s ease',
  },
};

// Apply media queries for responsive styles
if (typeof window !== 'undefined') {
  // Mobile styles
  if (window.innerWidth <= 768) {
    // Container styles
    styles.container.overflowY = 'auto';
    
    // Intro section
    styles.introSection.padding = '1.5rem';
    styles.heading.fontSize = '2rem';
    styles.introRow.flexDirection = 'column';
    styles.introRow.gap = '1rem';
    styles.introRow.minHeight = 'auto'; // Reset minHeight on mobile
    styles.introLeft.width = '100%';
    styles.introRight.width = '100%';
    styles.introRight.marginTop = '1rem';
    styles.paragraph.fontSize = '1rem';
  }
}
